import React, { useMemo } from 'react';
import { format } from 'date-fns';
import FilterChip from './FilterChip';
import { Icon, IconName } from '../../primitives/Icon';
import { PREDEFINED_TAGS } from '../../types/tags.types';
import './FilterDrawer.css';

interface ActiveFiltersBarProps {
  // Tags sélectionnés
  selectedTags: string[];
  onTagRemove: (tagId: string) => void;
  // Dates sélectionnées
  selectedDates: string[];
  onDateRemove: (date: string) => void;
  // Heures sélectionnées
  selectedTimes: string[];
  onTimeRemove: (time: string) => void;
  // Date range
  dateRangeStart?: Date | null;
  dateRangeEnd?: Date | null;
  onDateRangeClear?: () => void;
  // Texte de recherche
  searchText?: string;
  onSearchTextClear?: () => void;
  // Ville de recherche
  searchCity?: string;
  onSearchCityClear?: () => void;
  // Clear all
  onClearAll: () => void;
}

// Labels pour les filtres de date
const DATE_LABELS: Record<string, string> = {
  'today': 'Today',
  'tomorrow': 'Tomorrow',
  'this_weekend': 'This weekend',
  'this_week': 'This week',
  'next_week': 'Next week',
};

// Configuration pour les filtres d'heure avec icônes Lucide
const TIME_CONFIG: Record<string, { label: string; icon: IconName }> = {
  'morning': { label: 'Morning', icon: 'Sunrise' },
  'afternoon': { label: 'Afternoon', icon: 'Sun' },
  'evening': { label: 'Evening', icon: 'Sunset' },
};

// Mapping des tags d'activité vers leurs icônes Lucide
const TAG_ICONS: Record<string, IconName> = {
  'free': 'Gift',
  'music': 'Music',
  'sports': 'Trophy',
  'art': 'Palette',
  'food': 'Utensils',
  'culture': 'Landmark',
  'yoga': 'Heart',
  'spirituality': 'Sparkles',
  'children': 'Baby',
  'party': 'PartyPopper',
};

// Fonction pour formater une date custom (custom_2025-12-25 → Dec 25, 2025)
const formatCustomDate = (dateStr: string): string => {
  if (!dateStr.startsWith('custom_') && !dateStr.startsWith('day_')) return dateStr;

  const datePart = dateStr.substring(dateStr.indexOf('_') + 1);
  const date = new Date(datePart);

  if (isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const ActiveFiltersBar: React.FC<ActiveFiltersBarProps> = ({
  selectedTags,
  onTagRemove,
  selectedDates,
  onDateRemove,
  selectedTimes,
  onTimeRemove,
  dateRangeStart,
  dateRangeEnd,
  onDateRangeClear,
  searchText,
  onSearchTextClear,
  searchCity,
  onSearchCityClear,
  onClearAll,
}) => {
  // Construire les chips à afficher
  const chips = useMemo(() => {
    const result: Array<{ id: string; label: React.ReactNode; onRemove: () => void }> = [];

    // Ajouter les tags avec icônes Lucide
    selectedTags.forEach(tagId => {
      const tag = PREDEFINED_TAGS.find(t => t.id === tagId);
      if (tag) {
        const iconName = TAG_ICONS[tagId];
        result.push({
          id: `tag-${tagId}`,
          label: iconName ? (
            <>
              <Icon name={iconName} size={14} />
              {tag.label}
            </>
          ) : `${tag.icon} ${tag.label}`,
          onRemove: () => onTagRemove(tagId),
        });
      }
    });

    // Ajouter les dates avec icône Calendar
    selectedDates.forEach(date => {
      // Si c'est une date custom, la formater
      const dateLabel = (date.startsWith('custom_') || date.startsWith('day_'))
        ? formatCustomDate(date)
        : DATE_LABELS[date] || date;

      result.push({
        id: `date-${date}`,
        label: (
          <>
            <Icon name="Calendar" size={14} />
            {dateLabel}
          </>
        ),
        onRemove: () => onDateRemove(date),
      });
    });

    // Ajouter le date range
    if ((dateRangeStart || dateRangeEnd) && onDateRangeClear) {
      const rangeLabel = dateRangeStart && dateRangeEnd
        ? `${format(dateRangeStart, 'MMM d')} - ${format(dateRangeEnd, 'MMM d')}`
        : dateRangeStart
          ? `From ${format(dateRangeStart, 'MMM d')}`
          : `Until ${format(dateRangeEnd!, 'MMM d')}`;
      result.push({
        id: 'date-range',
        label: (
          <>
            <Icon name="CalendarRange" size={14} />
            {rangeLabel}
          </>
        ),
        onRemove: onDateRangeClear,
      });
    }

    // Ajouter les heures avec icônes Lucide
    selectedTimes.forEach(time => {
      const config = TIME_CONFIG[time];
      result.push({
        id: `time-${time}`,
        label: config ? (
          <>
            <Icon name={config.icon} size={14} />
            {config.label}
          </>
        ) : time,
        onRemove: () => onTimeRemove(time),
      });
    });

    // Ajouter la recherche texte avec icône Search
    if (searchText && searchText.trim() && onSearchTextClear) {
      result.push({
        id: 'search-text',
        label: (
          <>
            <Icon name="Search" size={14} />
            "{searchText}"
          </>
        ),
        onRemove: onSearchTextClear,
      });
    }

    // Ajouter la ville avec icône MapPin
    if (searchCity && searchCity.trim() && onSearchCityClear) {
      result.push({
        id: 'search-city',
        label: (
          <>
            <Icon name="MapPin" size={14} />
            {searchCity}
          </>
        ),
        onRemove: onSearchCityClear,
      });
    }

    return result;
  }, [
    selectedTags,
    onTagRemove,
    selectedDates,
    onDateRemove,
    selectedTimes,
    onTimeRemove,
    searchText,
    onSearchTextClear,
    searchCity,
    onSearchCityClear,
  ]);

  // Ne pas afficher la barre si aucun filtre actif
  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="active-filters-bar">
      <div className="active-filters-bar__chips">
        {chips.map(chip => (
          <FilterChip
            key={chip.id}
            label={chip.label}
            onRemove={chip.onRemove}
          />
        ))}
      </div>

      {chips.length > 1 && (
        <FilterChip
          label="Clear all"
          onRemove={onClearAll}
          showRemoveButton={false}
        />
      )}
    </div>
  );
};

export default ActiveFiltersBar;
